import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import service from '@/services';
import { ROOT_TAG_ALIAS } from '@/services/tag/converters';
import type { TreeDataState } from '@/stores/InsightExplorerStore';
import { TAXONOMY_CATEGORY, type TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { getLv1TagNodes, getTagPath } from '@/utils/tag';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import type { HierarchyNodeData } from '../components/InsightExplorerSidebar/HierarchyNavigator/HierarchyNavigator';
import { InsightExplorerByType } from '../types/constants';

export const fetchTagTree = async (
  tagMap: Record<string, TagT>,
): Promise<TreeDataState> => {
  const map: Record<string, HierarchyNodeData> = {};
  Object.entries(tagMap).forEach(([alias, node]) => {
    const paths = getTagPath(tagMap, alias).map((tag) => ({
      value: tag.alias,
      label: tag.name,
    }));
    paths.unshift({
      value: ROOT_TAG_ALIAS,
      label: 'All tags',
    });
  });
  const lv1 = getLv1TagNodes(tagMap).map((t) => t.alias);
  const mapper = (alias: string): HierarchyNodeData => {
    const tagItem = tagMap[alias];
    const paths = getTagPath(tagMap, alias).map((tag) => ({
      value: tag.alias,
      label: tag.name,
    }));
    paths.unshift({
      value: ROOT_TAG_ALIAS,
      label: 'All tags',
    });
    const node = {
      id: tagItem.alias,
      name: tagItem.name,
      path: paths,
      children: tagItem.children.map(mapper),
      node_type: InsightExplorerByType.TAG,
    };
    map[alias] = node;
    return node;
  };

  const rootNode = {
    id: ROOT_TAG_ALIAS,
    name: 'All tags',
    children: lv1.map(mapper),
    exceptSelf: true,
    node_type: InsightExplorerByType.TAG,
    path: [
      {
        value: ROOT_TAG_ALIAS,
        label: 'All tags',
      },
    ],
  };

  const data = [rootNode];
  map[ROOT_TAG_ALIAS] = rootNode;

  return {
    data,
    map,
  };
};

export const fetchTaxonomyTree = async (): Promise<TreeDataState> => {
  const data: HierarchyNodeData[] = [];
  const map: Record<string, HierarchyNodeData> = {};
  const taxonomies = await service.taxonomy.fetchTaxonomyList(
    getCurrWorkspaceAlias(),
  );
  const sortedTaxonomies = taxonomies.sort((a, b) => {
    if (b.category === TAXONOMY_CATEGORY.SAVED_TOPIC) return -1;
    if (a.category === TAXONOMY_CATEGORY.SAVED_TOPIC) return 1;
    return 0;
  });

  for (const taxonomy of sortedTaxonomies) {
    const topics = await service.taxonomy.fetchTaxonomyTopicList(
      taxonomy.alias,
    );
    const topicMap = topics.reduce(
      (acc, topic) => {
        acc[topic.alias] = topic;
        return acc;
      },
      {} as Record<string, Topic2WithoutCount<TOPIC_2_TYPE>>,
    );

    const topicMapper = (
      topic: Topic2WithoutCount<TOPIC_2_TYPE>,
    ): HierarchyNodeData => {
      const path = taxonomyUtils.getTopicPath({
        ...topic,
      });
      path.unshift({
        value: taxonomy.alias,
        label: taxonomy.title,
        isTaxonomy: true,
      });
      path.push({
        value: topic.alias,
        label: topic.name,
        isTaxonomy: false,
      });
      const topicNode = {
        id: topic.alias,
        name: topic.name,
        path: path.map((t) => ({
          value: t.value,
          label: t.label,
        })),
        children: topic.children?.map((alias) => topicMapper(topicMap[alias])),
        node_type: InsightExplorerByType.TAXONOMY_TOPIC,
      };
      map[topicNode.id] = topicNode;
      return topicNode;
    };

    const rootTopics = topics.filter((topic) => topic.level === 1);

    const taxonomyNode = {
      id: taxonomy.alias,
      name: taxonomy.title,
      path: [
        {
          value: taxonomy.alias,
          label: taxonomy.title,
        },
      ],
      children: rootTopics.map(topicMapper),
      exceptSelf: taxonomy.category === TAXONOMY_CATEGORY.SAVED_TOPIC,
      node_type: InsightExplorerByType.TAXONOMY,
    };
    map[taxonomyNode.id] = taxonomyNode;
    data.push(taxonomyNode);
  }

  return {
    data,
    map,
  };
};
